import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LogOut, Settings as SettingsIcon } from 'lucide-react';
import { CONTENT_WIDTH } from '../new-components/layout.constants';

interface NavItem {
  label: string;
  href: string;
  icon?: React.ReactNode;
}

interface MobileNavProps {
  isOpen: boolean;
  onClose: () => void;
  /** Primary sections — same set/labels as the desktop tab bar. */
  navItems: Array<NavItem>;
  /** Account/utility links (e.g. Settings), shown below the divider. */
  accountItems?: Array<NavItem>;
  user?: { name: string; email: string; avatar?: string } | null;
  onLogout?: () => void;
  /** Where the panel starts from the top — directly below the sticky tab bar, which stays visible. */
  topOffset?: number;
}

/**
 * Mobile navigation panel (mobile only; desktop uses the inline tab bar).
 * This is an extension of the tab bar itself: it slides down from behind the sticky header,
 * shares the header's grey surface, container alignment, and text sizing, and uses the same
 * color-only active/hover treatment (muted -> foreground) — no background highlights.
 */
export const MobileNav: React.FC<MobileNavProps> = ({
  isOpen,
  onClose,
  navItems,
  accountItems = [],
  user,
  onLogout,
  topOffset = 0,
}) => {
  const location = useLocation();
  const scrollRef = useRef<HTMLElement>(null);
  const isActive = (href: string): boolean => location.pathname.startsWith(href);

  // Lock background scroll while the panel is open without touching body overflow (which
  // unsticks the header). Swallow touchmove + wheel everywhere except inside the nav's scroll
  // area, and there only while it can still scroll that direction.
  useEffect(() => {
    if (!isOpen) return;
    const consumedByNav = (target: Node | null, deltaY: number): boolean => {
      const el = scrollRef.current;
      if (!el || !target || !el.contains(target)) return false;
      const canScroll = el.scrollHeight > el.clientHeight;
      if (!canScroll) return false;
      const atTop = el.scrollTop <= 0;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
      // Let the nav scroll unless the gesture would push past its top/bottom edge (which would
      // otherwise chain to the page). deltaY is 0 for touch — rely on overscroll-contain there.
      if (deltaY < 0) return !atTop;
      if (deltaY > 0) return !atBottom;
      return true;
    };
    const onWheel = (e: WheelEvent) => {
      if (!consumedByNav(e.target as Node, e.deltaY)) e.preventDefault();
    };
    const onTouchMove = (e: TouchEvent) => {
      if (!consumedByNav(e.target as Node, 0)) e.preventDefault();
    };
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('wheel', onWheel);
    };
  }, [isOpen]);

  const rowText = 'text-base font-medium transition-colors';

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="mobile-nav"
          initial={{ y: '-100%' }}
          animate={{ y: 0 }}
          exit={{ y: '-100%' }}
          transition={{ type: 'tween', duration: 0.25, ease: 'easeOut' }}
          className="fixed inset-x-0 bottom-0 z-40 bg-secondary md:hidden portal-typography"
          style={{ top: topOffset }}
        >
          <div className={`${CONTENT_WIDTH} flex h-full flex-col py-2`}>
            {/* Primary sections — same labels/sizing as the desktop tab bar */}
            <nav ref={scrollRef} className="flex-1 min-h-0 flex flex-col overflow-y-auto overscroll-contain">
              {navItems.map((item) => {
                const active = isActive(item.href);
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={onClose}
                    aria-current={active ? 'page' : undefined}
                    className={`block py-3 ${rowText} ${
                      active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>

            {/* Account section — divider, then utility links + log out */}
            {(user || accountItems.length > 0 || onLogout) && (
              <div className="mt-auto border-t border-border pt-2 pb-[max(theme(spacing.4),env(safe-area-inset-bottom))]">
                {user && (
                  <p className="py-2 text-xs text-muted-foreground truncate">{user.email}</p>
                )}
                {accountItems.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={onClose}
                    className={`flex items-center gap-3 py-3 text-muted-foreground hover:text-foreground ${rowText}`}
                  >
                    <span className="w-4 h-4 flex-shrink-0">
                      {item.icon ?? <SettingsIcon className="w-4 h-4" />}
                    </span>
                    {item.label}
                  </Link>
                ))}
                {onLogout && (
                  <button
                    type="button"
                    onClick={() => {
                      onLogout();
                      onClose();
                    }}
                    className={`flex w-full items-center gap-3 py-3 text-muted-foreground hover:text-foreground ${rowText}`}
                  >
                    <LogOut className="w-4 h-4 flex-shrink-0" />
                    Log out
                  </button>
                )}
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MobileNav;
